import translationsDefault from "../../locales/translation.json";
import translationsEN from "../../locales/en/translation.json";
import translationsNL from "../../locales/nl/translation.json";
import translationsES from "../../locales/es/translation.json";
import translationsDE from "../../locales/de/translation.json";
import translationsPT from "../../locales/pt/translation.json";
import translationsFR from "../../locales/fr/translation.json";
import translationsCA from "../../locales/ca/translation.json";
import translationsLOL from "../../locales/lol/translation.json";
import currenciesDefault from "../../locales/currency.json";
import currenciesEN from "../../locales/en/currency.json";
import currenciesNL from "../../locales/nl/currency.json";
import currenciesES from "../../locales/es/currency.json";
import currenciesDE from "../../locales/de/currency.json";
import currenciesPT from "../../locales/pt/currency.json";
import currenciesFR from "../../locales/fr/currency.json";
import currenciesCA from "../../locales/ca/currency.json";
import type { Accessor, ContextProviderComponent } from "solid-js";
import { createContext, createResource, createSignal } from "solid-js";
import type { SelectorKey, Resource, ResourceLanguage, TFunction } from "i18next";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initializedContext } from "../utils/initializedUtil";
import environment from "../environment";

export const CURRENCY_NAMESPACE = "currency";
const CROWDIN = "lol";
const SOURCE = "dev";
const NAMESPACE = "translation";

// i18n uses the language code without region subtag for language detection & fallbacks
// e.g. "en" instead of "en-US"
const LANGUAGES = {
  English: "en",
  Dutch: "nl",
  German: "de",
  Spanish: "es",
  Portuguese: "pt",
  French: "fr",
  Catalan: "ca",
  CROWDIN: "lol",
} as const;
export type LanguageCode = (typeof LANGUAGES)[keyof typeof LANGUAGES];

const resources: Resource = {
  [SOURCE]: { translation: translationsDefault, currency: currenciesDefault },
  [LANGUAGES.English]: { translation: translationsEN, currency: currenciesEN },
  [LANGUAGES.Dutch]: { translation: translationsNL, currency: currenciesNL },
  [LANGUAGES.Spanish]: { translation: translationsES, currency: currenciesES },
  [LANGUAGES.German]: { translation: translationsDE, currency: currenciesDE },
  [LANGUAGES.Portuguese]: { translation: translationsPT, currency: currenciesPT },
  [LANGUAGES.French]: { translation: translationsFR, currency: currenciesFR },
  [LANGUAGES.Catalan]: { translation: translationsCA, currency: currenciesCA },
  ...(environment.isDevelopment()
    ? { [CROWDIN]: { [NAMESPACE]: translationsLOL as ResourceLanguage, currency: currenciesDefault } }
    : {}),
};

type I18nContextProps = {
  t: TFunction;
  changeLanguage: (language: LanguageCode) => Promise<void>;
  currentLanguage: Accessor<LanguageCode>;
};

export type TranslationKey = SelectorKey;

const I18nContext = createContext<I18nContextProps>();

export const initI18n = () =>
  i18n.use(LanguageDetector).init({
    fallbackLng: {
      [LANGUAGES.Catalan]: [LANGUAGES.Spanish, LANGUAGES.English, SOURCE],
      default: [LANGUAGES.English, SOURCE],
    },
    returnEmptyString: false,
    defaultNS: NAMESPACE,
    ns: [NAMESPACE, CURRENCY_NAMESPACE],
    resources,
    interpolation: {
      escapeValue: false, // not needed for SolidJS as it escapes by default
    },
  });

export const I18nProvider: ContextProviderComponent<undefined> = (props) => {
  const [translate, { mutate }] = createResource(() => initI18n(), {
    initialValue: (i18n.isInitialized ? i18n.t : () => "") as TFunction,
  });

  // The initial page load is not seen as a language change event
  if (i18n.isInitialized) {
    document.documentElement.setAttribute("lang", i18n.resolvedLanguage ?? LANGUAGES.English);
  }

  i18n.on("languageChanged", (language) => {
    document.documentElement.setAttribute("lang", language);
  });

  const [currentLanguage, setCurrentLanguage] = createSignal<LanguageCode>(
    (i18n.resolvedLanguage as LanguageCode) ?? LANGUAGES.English,
  );

  const changeLanguage = async (language: LanguageCode) => {
    if (language !== currentLanguage()) {
      setCurrentLanguage(language);
      const t = await i18n.changeLanguage(language);

      mutate(() => t); // Force re-render of all translations
    }
  };

  return (
    <I18nContext.Provider
      value={{
        t: ((...args: Parameters<TFunction>) => translate()(...args)) as TFunction,
        currentLanguage,
        changeLanguage,
      }}
    >
      {props.children}
    </I18nContext.Provider>
  );
};

export const useTranslation = () => initializedContext(I18nContext, "useTranslation", "I18nProvider");
